import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import useAdminPracticeQuestions, {
  AdminPracticeQuestion,
  PracticeQuestionFilters,
  useUpdatePracticeQuestion,
  useDeletePracticeQuestion,
} from '@/api/useAdminPracticeQuestions';
import { Database } from '@/_constants/database.types';

type CategoryCode = Database['public']['Enums']['category_code'];
import { useAdminQuestionCategories } from '@/api/useAdminQuestionCategories';
import DataTable from '@/components/functional/DataTable/DataTable';
import SelectInput from '@/components/functional/SelectInput/SelectInput';
import TextInput from '@/components/functional/TextInput/TextInput';
import AdminConfirmModal from '@/components/admin/AdminConfirmModal';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton';
import { ColumnDef, PaginationState } from '@tanstack/react-table';
import Routes from '@/router/Routes';
import { ContentStatus, CONTENT_STATUS_FILTER_OPTIONS, CONTENT_STATUS_BADGE_COLORS } from '@/_constants/statusOptions';
import CreatePracticeQuestionModal from '@/components/admin/CreatePracticeQuestionModal';

const SUBJECT_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All Subjects' },
  { value: 'English Language', label: 'English Language' },
  { value: 'English Literature', label: 'English Literature' },
];

const PAPER_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All Papers' },
  { value: 'Paper 1', label: 'Paper 1' },
  { value: 'Paper 2', label: 'Paper 2' },
];

const SESSION_KEY = 'adminPracticeQuestionsState';

const readSession = () => {
  try {
    return JSON.parse(sessionStorage.getItem(SESSION_KEY) || '{}');
  } catch {
    return {};
  }
};

const PracticeQuestionsPage: FC = () => {
  const navigate = useNavigate();
  const session = readSession();

  const [pagination, setPagination] = useState<PaginationState>(
    session.pagination ?? { pageIndex: 0, pageSize: 15 }
  );
  const [subjectFilter, setSubjectFilter] = useState<string>(session.subjectFilter ?? '');
  const [paperFilter, setPaperFilter] = useState<string>(session.paperFilter ?? '');
  const [filters, setFilters] = useState<PracticeQuestionFilters>(session.filters ?? {});
  const [searchInput, setSearchInput] = useState<string>(session.searchInput ?? '');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState<AdminPracticeQuestion | null>(null);

  // Persist filter state across navigation
  useEffect(() => {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ pagination, subjectFilter, paperFilter, filters, searchInput }));
  }, [pagination, subjectFilter, paperFilter, filters, searchInput]);

  const { data: categories = [] } = useAdminQuestionCategories();

  // Build category options from database
  const categoryCodeOptions = useMemo(() => {
    const filtered = categories.filter((cat) => {
      if (subjectFilter && cat.subject !== subjectFilter) return false;
      if (paperFilter && cat.paper_number !== paperFilter) return false;
      return true;
    });
    return [
      { value: '', label: 'All Categories' },
      ...filtered.map((cat) => ({
        value: cat.category_code,
        label: `${cat.category_code} (${cat.name})`,
      })),
    ];
  }, [categories, subjectFilter, paperFilter]);

  // Resolve subject/paper UI filters → category code array using question_categories data
  const resolvedCategoryCodes = useMemo(() => {
    if (!subjectFilter && !paperFilter) return undefined;
    const codes = categories
      .filter((c) => {
        if (subjectFilter && c.subject !== subjectFilter) return false;
        if (paperFilter && c.paper_number !== paperFilter) return false;
        return true;
      })
      .map((c) => c.category_code as CategoryCode);
    return codes.length > 0 ? codes : undefined;
  }, [categories, subjectFilter, paperFilter]);

  const { data, isFetching } = useAdminPracticeQuestions({
    pagination,
    filters: { ...filters, categoryCodes: resolvedCategoryCodes },
  });

  const { mutate: updateQuestion } = useUpdatePracticeQuestion();
  const { mutate: deleteQuestion, isPending: isDeleting } = useDeletePracticeQuestion();

  const handleFilterChange = <K extends keyof PracticeQuestionFilters>(key: K, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
    }));
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handleSearch = () => {
    handleFilterChange('search', searchInput);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClearFilters = () => {
    setSubjectFilter('');
    setPaperFilter('');
    setFilters({});
    setSearchInput('');
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handleStatusChange = useCallback(
    (question: AdminPracticeQuestion, newStatus: ContentStatus) => {
      updateQuestion({
        questionId: question.id,
        updates: { status: newStatus },
      });
    },
    [updateQuestion]
  );

  const handleDeleteConfirm = () => {
    if (questionToDelete) {
      deleteQuestion(questionToDelete.id, {
        onSuccess: () => setQuestionToDelete(null),
      });
    }
  };

  const questions = data?.data ?? [];
  const rowCount = data?.rowCount ?? 0;
  const pageCount = Math.ceil(rowCount / pagination.pageSize);

  const truncateContent = (content: string | null, maxLength = 80) => {
    if (!content) return 'No content';
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  const columns: ColumnDef<AdminPracticeQuestion>[] = useMemo(
    () => [
      {
        accessorKey: 'category_code',
        header: 'Category',
        enableSorting: false,
        cell: (info) => (
          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
            {(info.getValue() as string) || 'N/A'}
          </span>
        ),
      },
      {
        accessorKey: 'content',
        header: 'Content',
        enableSorting: false,
        cell: (info) => (
          <span className="text-gray-700 text-sm">{truncateContent(info.getValue() as string | null)}</span>
        ),
      },
      {
        accessorKey: 'marks',
        header: 'Marks',
        enableSorting: false,
        cell: (info) => <span className="text-gray-600 font-medium">{(info.getValue() as number) || 'N/A'}</span>,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex flex-col gap-1">
            <select
              value={row.original.status}
              onChange={(e) => handleStatusChange(row.original, e.target.value as ContentStatus)}
              className={`px-2 py-1 text-xs font-medium border-0 rounded-lg cursor-pointer ${CONTENT_STATUS_BADGE_COLORS[row.original.status]}`}
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
            {/* {row.original.mock_past_paper_id && (
              <span
                className="px-2 py-0.5 text-xs font-medium rounded bg-amber-100 text-amber-800 cursor-default"
                title={`Locked to mock paper: ${row.original.mock_past_paper_id}`}
              >
                Locked
              </span>
            )} */}
          </div>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(Routes.ADMIN_PRACTICE_QUESTIONS + '/' + row.original.id)}
              className="text-[#C87941] font-semibold hover:text-[#a66835] transition-colors"
            >
              Edit
            </button>
            <button
              onClick={() => setQuestionToDelete(row.original)}
              className="text-red-600 font-semibold hover:text-red-800 transition-colors"
            >
              Delete
            </button>
          </div>
        ),
      },
    ],
    [navigate, handleStatusChange]
  );

  const hasFilters = subjectFilter || paperFilter || filters.categoryCode || filters.status || filters.search;

  return (
    <div className="min-h-full">
      {/* Header */}
      <div className="bg-white rounded-3xl flex justify-between items-center px-8 py-6">
        <h1 className="text-3xl font-semibold text-gray-900">Practice Questions</h1>
        <LoadingButton
          onClick={() => setIsCreateModalOpen(true)}
          loading={false}
          className="flex items-center gap-2 px-4 py-2 bg-[#0f172a] text-white rounded-xl hover:bg-[#1e293b] transition-colors border-none"
        >
          <PlusIcon className="w-5 h-5" />
          <span>Add Question</span>
        </LoadingButton>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-3xl px-8 py-6 mt-6">
        <div className="grid grid-cols-5 gap-4">
          <SelectInput
            value={subjectFilter}
            onChange={(value) => {
              setSubjectFilter(value);
              setFilters((f) => ({ ...f, categoryCode: undefined }));
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
            options={SUBJECT_OPTIONS}
            label="Subject"
          />
          <SelectInput
            value={paperFilter}
            onChange={(value) => {
              setPaperFilter(value);
              setFilters((f) => ({ ...f, categoryCode: undefined }));
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
            options={PAPER_OPTIONS}
            label="Paper"
          />
          <SelectInput
            value={filters.categoryCode || ''}
            onChange={(value) => handleFilterChange('categoryCode', value)}
            options={categoryCodeOptions}
            label="Category"
          />
          <SelectInput
            value={filters.status || ''}
            onChange={(value) => handleFilterChange('status', value)}
            options={CONTENT_STATUS_FILTER_OPTIONS}
            label="Status"
          />
          <div className="flex gap-2 items-end">
            {hasFilters && (
              <button
                onClick={handleClearFilters}
                className="flex items-center gap-1 px-3 h-12 text-gray-600 hover:text-gray-800 transition-colors"
              >
                <XMarkIcon className="w-4 h-4" />
                Clear
              </button>
            )}
          </div>
        </div>
        <div className="flex gap-2 items-end mt-4">
          <div className="flex-1">
            <TextInput
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search by content..."
              label="Search Content"
              endIcon={<MagnifyingGlassIcon className="w-5 h-5 text-gray-400" />}
              bordered
            />
          </div>
          <LoadingButton
            onClick={handleSearch}
            loading={isFetching}
            className="px-4 h-12 bg-[#C87941] text-white rounded-lg hover:bg-[#a66835] transition-colors border-none"
          >
            Search
          </LoadingButton>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-3xl overflow-hidden shadow-sm mt-6">
        <DataTable
          data={questions}
          columns={columns}
          wrapperClassName="bg-white"
          headClassName="bg-gray-50 border-t border-gray-100"
          rowClassName="hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
          showPagination={true}
          manualPagination={true}
          paginationState={pagination}
          onPaginationChange={setPagination}
          pageCount={pageCount}
          rowCount={rowCount}
          loading={isFetching}
        />
      </div>

      {/* Create Modal */}
      <CreatePracticeQuestionModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} />

      {/* Delete Confirmation Modal */}
      {questionToDelete && (
        <AdminConfirmModal
          isOpen={true}
          onClose={() => setQuestionToDelete(null)}
          onConfirm={handleDeleteConfirm}
          variant="error"
          title="Delete Practice Question"
          message="Are you sure you want to delete this practice question? This action cannot be undone."
          warning={truncateContent(questionToDelete.content, 100)}
          confirmText="Delete"
          isLoading={isDeleting}
        />
      )}
    </div>
  );
};

export default PracticeQuestionsPage;
